import React, { useCallback, useState } from "react";
import moment from "moment";
import DayPickerSingleDateController from "react-dates/lib/components/DayPickerSingleDateController";
import {
  commonDatePickerProps,
  DefaultSetStatusText,
  getVisibleMonths,
} from "../common";
import type { TypeSingleDatePickerProps } from "./SingleDatePickerTypes";
import { VisuallyHidden } from "@sproutsocial/seeds-react-visually-hidden";

const noop = () => {};

const SingleDatePicker = ({
  onDateChange,
  setStatusText = DefaultSetStatusText,
  date = null,
  focused = false,
  onFocusChange = noop,
  initialVisibleMonth,
  numberOfMonths = 1,
  onBlur,
  ...rest
}: TypeSingleDatePickerProps) => {
  const [statusText, updateStatusText] = useState(() =>
    setStatusText(
      getVisibleMonths(
        moment(initialVisibleMonth?.() || date || undefined),
        numberOfMonths
      )
    )
  );

  const handleMonthClick = useCallback(
    // @ts-ignore unknown types
    (date) => {
      updateStatusText(setStatusText(getVisibleMonths(date, numberOfMonths)));
    },
    [numberOfMonths, setStatusText]
  );
  const wrappedOnBlur = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      onFocusChange?.({ focused: false });
      onBlur?.(event);
    },
    [onBlur, onFocusChange]
  );

  /*
   * The react-dates overrides that ReactDatesCssOverrides used to inject at
   * runtime now ship statically in datepicker.css, aggregated into
   * @sproutsocial/racine/css/components. styles.ts keeps the styled source for
   * Phase A, but nothing imports it, so it is tree-shaken out of the bundle.
   */
  return (
    <>
      <VisuallyHidden>
        <div role="status">{statusText}</div>
      </VisuallyHidden>
      <DayPickerSingleDateController
        {...commonDatePickerProps}
        date={date}
        numberOfMonths={numberOfMonths}
        onDateChange={onDateChange}
        initialVisibleMonth={initialVisibleMonth || null}
        focused={focused}
        onBlur={wrappedOnBlur}
        onFocusChange={onFocusChange}
        onPrevMonthClick={handleMonthClick}
        onNextMonthClick={handleMonthClick}
        {...rest}
      />
    </>
  );
};

export default React.memo<TypeSingleDatePickerProps>(SingleDatePicker);
